import { useState } from "react";
import { Box, Checkbox, Select, MenuItem, Tooltip } from "@mui/material";
import {
  PeopleAlt as ActiveIcon,
  EventBusy as ExpiringIcon,
  DoNotDisturbOn as InactiveIcon,
  WhereToVote as TrainedIcon,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
  Check as CheckIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";
import { GOAL_CUT, GOAL_GAIN } from "./coachHelpers";
import { enableClients, disableClients } from "../../../services/TraineeService";
import { toggleClientReview } from "../../../services/CoachDashboardService";

// Re-export the shared FilterChip under the name external modules import from here.
export { FilterChip };

// Format an epoch-seconds timestamp as DD/MM/YY for the תוקף מנוי / מדידות גוף cells.
// NOTE: intentionally NOT shared — this variant keys off `ts == null` (so ts=0 still
// formats) and has an explicit NaN guard, unlike the other tabs' date formatters.
export const fmtDate = (ts) => {
  if (ts == null) return "—";
  const d = new Date(ts * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
};

const NOW_S = () => Math.floor(Date.now() / 1000);
const DAYS = 86400;
export const isExpired = (ts) => ts != null && ts < NOW_S();
export const isExpiringSoon = (ts) => {
  const now = NOW_S();
  return ts != null && ts >= now && ts < now + 30 * DAYS;
};
// Color the weekly weight trend based on the trainee's target range. Gray ONLY
// when there's no usable signal (trend missing, or no goal/range set at all).
// Falls back to goal-name semantics if the row didn't carry target bounds.
// NOTE: 4-arg, range-priority — genuinely different from WeightTab's goal-aware color,
// so it stays local.
export const weightTrendColor = (goal, trend, targetMin, targetMax) => {
  if (trend == null) return t.t3;

  if (targetMin != null && targetMax != null) {
    const lo = Math.min(targetMin, targetMax);
    const hi = Math.max(targetMin, targetMax);
    return trend >= lo && trend <= hi ? t.green : t.red;
  }

  if (GOAL_CUT.has(goal)) return trend < 0 ? t.green : t.red;
  if (GOAL_GAIN.has(goal)) return trend > 0 ? t.green : t.red;
  return t.t3;
};

export const ClientsFilters = ({ state, onChange }) => (
  <>
    <FilterChip
      active={state === "ACTIVE"}
      onClick={() => onChange(state === "ACTIVE" ? null : "ACTIVE")}
      color={t.green}
      label={<><ActiveIcon sx={{ fontSize: 12 }} /> פעיל</>}
    />
    <FilterChip
      active={state === "IN_ACTIVE"}
      onClick={() => onChange(state === "IN_ACTIVE" ? null : "IN_ACTIVE")}
      color={t.t3}
      label={<><InactiveIcon sx={{ fontSize: 12 }} /> לא פעיל</>}
    />
    <FilterChip
      active={state === "CLOSE_TO_END"}
      onClick={() => onChange(state === "CLOSE_TO_END" ? null : "CLOSE_TO_END")}
      color={t.t2}
      label={<><ExpiringIcon sx={{ fontSize: 12 }} /> עומד להסיים</>}
    />
    <FilterChip
      active={state === "DAYS_SINCE_30_PLUS"}
      onClick={() => onChange(state === "DAYS_SINCE_30_PLUS" ? null : "DAYS_SINCE_30_PLUS")}
      color={t.red}
      label={<><InactiveIcon sx={{ fontSize: 12 }} /> נעדרים</>}
    />
    <FilterChip
      active={state === "ACTIVE_THIS_WEEK"}
      onClick={() => onChange(state === "ACTIVE_THIS_WEEK" ? null : "ACTIVE_THIS_WEEK")}
      color={t.blue}
      label={<><TrainedIcon sx={{ fontSize: 12 }} /> התאמנו השבוע</>}
    />
  </>
);

const cell = { padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 12px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align }) => {
  const active = currentKey === sortKey;
  const effectiveAlign = align || "center";
  const flexJustify = effectiveAlign === "center" ? "center" : effectiveAlign === "start" ? "flex-start" : "flex-end";
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: effectiveAlign,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: flexJustify, gap: "2px" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const ReviewToggle = ({ traineeId, reviewed, onToggled }) => {
  const [busy, setBusy] = useState(false);
  const click = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await toggleClientReview(traineeId);
      onToggled?.(res.data?.reviewedThisWeek === true);
    } catch (err) {
      // swallow
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip title={reviewed ? "סמן כלא נבדק" : "סמן כנבדק"} arrow>
      <Box
        component="button"
        onClick={click}
        sx={{
          width: 26, height: 26, borderRadius: "50%",
          border: `1.5px solid ${reviewed ? t.green : t.border}`,
          background: reviewed ? t.green : "transparent",
          color: reviewed ? "#fff" : t.t3,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          cursor: busy ? "wait" : "pointer",
          transition: "all .14s",
          padding: 0,
          fontFamily: "inherit",
        }}
      >
        <CheckIcon sx={{ fontSize: 14 }} />
      </Box>
    </Tooltip>
  );
};

const ClientsTab = ({
  rows, selected, onToggleSelect, onToggleAll,
  sortKey, sortDir, onSortChange,
  onOpenProfile, onOpenActions, onRowChanged,
}) => {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  const onStatusChange = async (traineeId, newValue) => {
    try {
      if (newValue === "active") {
        await enableClients([traineeId]);
      } else {
        await disableClients([traineeId]);
      }
      onRowChanged?.({ traineeId, enabled: newValue === "active" });
    } catch (e) {
      // swallow
    }
  };


  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh label="שם לקוח" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} align="start" />
          <SortableTh label="סטטוס" sortKey="enabled" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="אימונים השבוע" sortKey="workoutsDone" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="ממוצע משקל" sortKey="weightAvg" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="מגמה שבועית" sortKey="weightTrend" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="ממוצע צעדים" sortKey="stepsAvg" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="תוקף מנוי" sortKey="expirationDate" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="מדידות גוף" sortKey="lastBodyMeasurementDate" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}></Box>
          <Box component="th" sx={thBase}>פעולות</Box>
          <SortableTh label="נבדק" sortKey="reviewedThisWeek" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isChecked = selected.has(r.traineeId);
          const dim = r.reviewedThisWeek;
          // workouts
          const wDone = r.workoutsDone || 0;
          const wGoal = r.workoutsGoal || 0;
          const wMet = wGoal > 0 && wDone >= wGoal;
          // steps
          const stepsOk = r.stepsAvg != null && r.stepsGoal != null && r.stepsAvg >= r.stepsGoal;
          // weight target text
          const targetText = (r.weightTargetMin != null && r.weightTargetMax != null)
            ? `(${r.weightTargetMin > 0 ? "+" : ""}${r.weightTargetMin} / ${r.weightTargetMax > 0 ? "+" : ""}${r.weightTargetMax})`
            : null;
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s, opacity .15s",
                opacity: dim ? 0.55 : 1,
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, whiteSpace: "nowrap", textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Select
                  size="small"
                  value={r.enabled ? "active" : "inactive"}
                  onChange={(e) => onStatusChange(r.traineeId, e.target.value)}
                  sx={{
                    fontSize: 11.5, fontWeight: 600,
                    background: r.enabled ? t.greenLight : t.redLight,
                    color: r.enabled ? t.greenText : t.redText,
                    borderRadius: "20px",
                    "& .MuiSelect-select": { padding: "2px 20px 2px 8px" },
                    "& fieldset": { border: "none" },
                  }}
                >
                  <MenuItem value="active" sx={{ fontSize: 12 }}>פעיל</MenuItem>
                  <MenuItem value="inactive" sx={{ fontSize: 12 }}>לא פעיל</MenuItem>
                </Select>
              </Box>
              <Box component="td" sx={cell}>
                {wGoal > 0 ? (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    <Box component="strong" sx={{ color: wMet ? t.green : t.text }}>{wDone}</Box>
                    <Box component="span" sx={{ color: t.t2, fontSize: 11 }}> / {wGoal}</Box>
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {r.weightAvg != null ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 600 }}>{r.weightAvg.toFixed(1)}</Box>
                    <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> ק"ג</Box>
                  </>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {r.weightTrend != null ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", flexWrap: "wrap" }}>
                    {targetText && (
                      <Box component="span" dir="ltr" sx={{ fontSize: 10, color: t.t3, whiteSpace: "nowrap" }}>
                        {targetText}
                      </Box>
                    )}
                    <Box component="span" sx={{
                      fontSize: 13, fontWeight: 800,
                      color: weightTrendColor(r.goal, r.weightTrend, r.weightTargetMin, r.weightTargetMax),
                    }}>
                      {r.weightTrend > 0 ? "+" : ""}{r.weightTrend.toFixed(1)}
                    </Box>
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {r.stepsAvg != null ? (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    <Box component="strong" sx={{ color: stepsOk ? t.green : t.text }}>
                      {r.stepsAvg.toLocaleString()}
                    </Box>
                    {r.stepsGoal ? (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / {r.stepsGoal.toLocaleString()}</Box>
                    ) : (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / -</Box>
                    )}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <Box
                  component="span"
                  dir="ltr"
                  sx={{
                    display: "inline-block",
                    color: isExpired(r.expirationDate)
                      ? t.redText
                      : isExpiringSoon(r.expirationDate)
                      ? t.text
                      : t.t2,
                    fontWeight: isExpired(r.expirationDate) || isExpiringSoon(r.expirationDate) ? 600 : 400,
                  }}
                >
                  {fmtDate(r.expirationDate)}
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                <Box component="span" dir="ltr" sx={{ display: "inline-block", color: t.t2 }}>
                  {fmtDate(r.lastBodyMeasurementDate)}
                </Box>
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                {r.hasNotes && (
                  <Tooltip title="יש הערה על הלקוח" arrow>
                    <Box
                      onClick={() => onOpenProfile?.(r)}
                      sx={{
                        width: 22, height: 22, borderRadius: "50%",
                        background: t.bg, border: `1px solid ${t.border}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        cursor: "pointer", margin: "0 auto",
                      }}
                    >
                      <WarningIcon sx={{ fontSize: 12, color: t.t2 }} />
                    </Box>
                  </Tooltip>
                )}
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28, height: 28, borderRadius: "7px",
                    border: `1.5px solid ${t.border}`, background: t.bg,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer", color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                <ReviewToggle
                  traineeId={r.traineeId}
                  reviewed={r.reviewedThisWeek}
                  onToggled={(now) => onRowChanged?.({ traineeId: r.traineeId, reviewedThisWeek: now })}
                />
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default ClientsTab;
